import { List, ListItem, ListItemButton, ListItemIcon, ListItemText, styled, Divider, IconButton, Box, Toolbar, Tooltip, Typography, Menu, MenuItem } from '@mui/material';
import MuiDrawer from '@mui/material/Drawer';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { useTheme } from '@mui/material/styles';
import { useState, Fragment, useContext, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import FiberNewIcon from '@mui/icons-material/FiberNew';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import ExcelIcon from './ExcelIcon';
import UsFlagIcon from '../static/us.svg';
import SgFlagIcon from '../static/sg.svg';
import HkFlagIcon from '../static/hk.svg';
import JpFlagIcon from '../static/jp.svg';
import { brand } from '../getTheme/getTheme';

import { SubMenuContext } from '../Contexts';

const drawerWidth = 240;

const openedMixin = (theme) => ({
    width: drawerWidth,
    transition: theme.transitions.create('width', {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.enteringScreen,
    }),
    overflowX: 'hidden',
});

const closedMixin = (theme) => ({
    transition: theme.transitions.create('width', {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.leavingScreen,
    }),
    overflowX: 'hidden',
    width: `calc(${theme.spacing(7)} + 1px)`,
    [theme.breakpoints.up('sm')]: {
        width: `calc(${theme.spacing(8)} + 1px)`,
    },
});


const DrawerHeader = styled('div')(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    padding: theme.spacing(0, 1),
    // necessary for content to be below app bar
    ...theme.mixins.toolbar,
}));


const Drawer = styled(MuiDrawer, { shouldForwardProp: (prop) => prop !== 'open' })(
    ({ theme }) => ({
        width: drawerWidth,
        flexShrink: 0,
        whiteSpace: 'nowrap',
        boxSizing: 'border-box',
        variants: [
            {
                props: ({ open }) => open,
                style: {
                    ...openedMixin(theme),
                    '& .MuiDrawer-paper': openedMixin(theme),
                },
            },
            {
                props: ({ open }) => !open,
                style: {
                    ...closedMixin(theme),
                    '& .MuiDrawer-paper': closedMixin(theme),
                },
            },
        ],
    }),
);


export function DrawerMenu({ title, navEntries, open }) {    
    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
            {title && (
                <Typography variant="body2" sx={{ pt: 2, fontSize: '0.7rem', fontWeight: 500 }}>
                    {title}
                </Typography>
            )}
            <List sx={{ width: '100%' }}>
                {navEntries.map((entry, index) => (
                    <ListItem key={entry.label} disablePadding sx={{ display: 'block' }}>
                        <Tooltip
                            placement="right"
                            title={!open ? (
                                <Fragment>
                                    <span style={{ fontWeight: 'bold' }}>{entry.label}</span>
                                    {entry.desc && (
                                        <Fragment>
                                            <br />
                                            <span>{entry.desc}</span>
                                        </Fragment>
                                    )}
                                </Fragment>
                            ) : null}
                        >
                            <ListItemButton
                                sx={[
                                    { minHeight: 48, px: 2.5 },
                                    open ? { justifyContent: 'initial' } : { justifyContent: 'center' },
                                ]}
                                ref={entry.ref}
                                onClick={entry.onClick}
                            >
                                <ListItemIcon
                                    sx={[
                                        { minWidth: 0, justifyContent: 'center' },
                                        open ? { mr: 3 } : { mr: 'auto' },
                                    ]}
                                >
                                    {entry.icon}
                                </ListItemIcon>
                                <ListItemText
                                    primary={entry.label}
                                    secondary={
                                        open && entry.desc ? (
                                            <span
                                                style={{
                                                    fontSize: '0.9em',
                                                    whiteSpace: 'normal',
                                                    wordBreak: 'break-word',
                                                    // Instead of maxWidth, use a fixed width matching the fully open drawer
                                                    width: `${drawerWidth - 100}px`,
                                                    display: 'inline-block',
                                                    transition: 'opacity 0.2s',
                                                    opacity: open ? 1 : 0,
                                                }}
                                            >
                                                {entry.desc}
                                            </span>
                                        ) : null
                                    }
                                    sx={[open ? { opacity: 1 } : { opacity: 0 }]}
                                />
                            </ListItemButton>
                        </Tooltip>
                    </ListItem>
                ))}
            </List>
        </Box>
    )
}

function RegionPicker({ open, onDrawerToggle }) {
    const [anchorEl, setAnchorEl] = useState(null);
    const menuOpen = Boolean(anchorEl);
    
    const regionOptions = [
        { code: 'US', name: 'United States', flag: UsFlagIcon },
        { code: 'SG', name: 'Singapore', flag: SgFlagIcon },
        { code: 'HK', name: 'Hong Kong', flag: HkFlagIcon },
        { code: 'JP', name: 'Japan', flag: JpFlagIcon }
    ];

    // Determine current region based on URL
    const getCurrentRegion = () => {
        const geo = process.env.REACT_APP_GEO;
        return geo.toUpperCase(); // Default to US
    };

    const handleRegionClick = (regionCode) => {
        const currentPath = window.location.pathname;        
        const wwwUrl = process.env.REACT_APP_WWW_URL || 'https://www.revelata.com';
        let newUrl;
        
        if (regionCode === 'US') {
            newUrl = `${wwwUrl}${currentPath}`;
        } else {
            // Replace www. or webapp. with region subdomain
            const regionUrl = wwwUrl.replace(/(www\.|webapp\.)/, `${regionCode.toLowerCase()}.`);
            newUrl = `${regionUrl}${currentPath}`;
        }
        
        window.location.href = newUrl;
        setAnchorEl(null);
    };

    const handleMenuClick = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
    };

    const currentRegion = getCurrentRegion();
    const currentRegionData = regionOptions.find(r => r.code === currentRegion);

    const regionEntry = {
        label: "Geography",
        icon: <img 
            src={currentRegionData?.flag || UsFlagIcon} 
            alt={`${currentRegion} Flag`} 
            style={{ width: 24, height: 24 }} 
        />,
        desc: "Browse equities listed in this country.",
        onClick: handleMenuClick,
        ref: null
    };

    return (
        <>
            <DrawerMenu navEntries={[regionEntry]} open={open} />
            <Menu
                anchorEl={anchorEl}
                open={menuOpen}
                onClose={handleMenuClose}
                anchorOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'left',
                }}
                sx={{
                    '& .MuiPaper-root': {
                        minWidth: 200,
                    }
                }}
            >
                {regionOptions.map((region) => (
                    <MenuItem 
                        key={region.code}
                        onClick={() => handleRegionClick(region.code)}
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1
                        }}
                    >
                        <img 
                            src={region.flag} 
                            alt={`${region.code} Flag`} 
                            style={{ width: 20, height: 20 }} 
                        />
                        {region.name}
                    </MenuItem>
                ))}
            </Menu>
        </>
    );
}



export function LeftDrawer({ sx }) {
    const theme = useTheme();
    const [open, setOpen] = useState(false);
    const { subMenu, setSubMenu, setPassthruDrawerOpenState } = useContext(SubMenuContext);
    const navigate = useNavigate();

    const handleDrawerToggle = () => {
        setOpen(!open);
    };

    useEffect(() => {
        setPassthruDrawerOpenState(open);
    }, [open]);

    const navEntries = [
        { label: "Quick Start", icon: <RocketLaunchIcon />, desc: null, onClick: () => { setSubMenu(null); navigate("/launchpad") } },
        // { label: "Recent updates", icon: <FiberNewIcon />, desc: "Browse recent filings & earnings calls", onClick: () => { setSubMenu(null); navigate("/deepkpi/newsfeed") } },
        { label: "deepAnalysis", icon: <SmartToyIcon />, desc: "Chat with our AI equities analyst team", onClick: () => { setSubMenu(null); navigate("/deepanalysis/app") } },
        // { label: "deepInsight", icon: <DescriptionOutlinedIcon />, desc: "Get analyst reports & pre-built Excel models", onClick: () => { setSubMenu(null); navigate("/deepinsight/app") } },
        { label: "deepKPI", icon: <TrendingUpIcon />, desc: "Deep-dive into company KPI time series & tables", onClick: () => { setSubMenu(null); navigate("/deepkpi/app") } },
        { label: "Excel Add-in", icon: <ExcelIcon />, desc: "Install the deepKPI Excel add-in", onClick: () => { setSubMenu(null); window.open(process.env.REACT_APP_EXCELADDIN_URL) } },
        // { label: "deepInsight", route: "/deepInsight", icon: <ArticleIcon />, desc: "Pre-built Excel models & research reports" },
    ]

    return (
        <Drawer variant="permanent" open={open} sx={sx}>
            <Toolbar />
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: open ? 'flex-end' : 'center', width: '100%', position: 'relative' }}>
                <IconButton onClick={handleDrawerToggle}>
                    {open ? <ChevronLeftIcon /> : <ChevronRightIcon />}
                </IconButton>
            </Box>
            <Divider sx={{ borderColor: 'grey.800' }} />
            <RegionPicker open={open} onDrawerToggle={handleDrawerToggle} />
            <Divider sx={{ borderColor: 'grey.800' }} />
            <DrawerMenu navEntries={navEntries} open={open} />
            {subMenu && (
                <>
                    <Divider sx={{ borderColor: 'grey.800' }} />
                    {subMenu}
                </>
            )}
        </Drawer>
    );
}

export default LeftDrawer;